import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Download, Loader2, Link2, Camera } from "lucide-react";
import { getShare, unlockShare } from "../lib/api";
import { DeepAnalysisHero, DeepAnalysisReport } from "./DeepAnalysis";
import { BisnisReport, IklanReport } from "../components/potensiReports";
import { openDeepAnalysisPrint } from "../lib/printTicket";

// /laporan/:token — a shared report, no login. Renders the snapshot in the share
// with the same components the app uses, read-only: no radius switch, no refresh,
// no bucket. A password-protected link asks once; the server then sets a cookie
// scoped to this link's API path.

const KIND_LABEL = { klasik: "Analisis Mendalam", potensi_bisnis: "Potensi Bisnis", potensi_iklan: "Potensi Iklan" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

function Shell({ children }) {
  return (
    <div className="h-screen overflow-auto bg-slate-100">
      {children}
    </div>
  );
}

function Notice({ icon: Icon, title, text, tone = "slate" }) {
  const c = tone === "red" ? "bg-red-50 border-red-100 text-red-500" : "bg-slate-50 border-slate-100 text-slate-400";
  return (
    <Shell>
      <div className="min-h-full flex flex-col items-center justify-center px-6 text-center">
        <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center mb-3 ${c}`}><Icon className="w-5 h-5" /></div>
        <p className="text-sm font-bold text-slate-800">{title}</p>
        {text && <p className="text-xs text-slate-500 mt-1 max-w-sm leading-relaxed">{text}</p>}
        <a href="https://landhep.id" className="mt-6 text-[11px] font-bold text-brand-700 hover:underline">Tentang Landhep →</a>
      </div>
    </Shell>
  );
}

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);       // server answer
  const [error, setError] = useState(null);     // { status, message }
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockErr, setUnlockErr] = useState(null);

  const load = () => {
    setError(null);
    getShare(token).then(setData).catch((e) => setError({ message: e.message }));
  };
  useEffect(load, [token]);

  useEffect(() => {
    if (data?.title) document.title = `${data.title} · Landhep`;
  }, [data?.title]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true); setUnlockErr(null);
    try { await unlockShare(token, password); load(); }
    catch (e2) { setUnlockErr(e2.message); }
    finally { setUnlocking(false); }
  };

  if (error) {
    return <Notice icon={Link2} tone="red" title="Tautan tidak bisa dibuka" text={error.message} />;
  }
  if (!data) {
    return <Notice icon={Loader2} title="Memuat laporan…" />;
  }

  if (data.locked) {
    return (
      <Shell>
        <div className="min-h-full flex items-center justify-center px-6">
          <form onSubmit={unlock} className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-sm p-7">
            <div className="w-11 h-11 rounded-xl bg-[#e3eae8] flex items-center justify-center mb-4"><Lock className="w-5 h-5 text-[#14543e]" /></div>
            <p className="text-sm font-bold text-slate-800">{data.title}</p>
            <p className="text-xs text-slate-500 mt-1">{KIND_LABEL[data.kind]}{data.ownerLabel ? ` · dibagikan oleh ${data.ownerLabel}` : ""}. Laporan ini dilindungi kata sandi.</p>
            <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Kata sandi"
              className="mt-4 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-500" />
            {unlockErr && <p className="mt-2 text-xs text-red-600">{unlockErr}</p>}
            <button type="submit" disabled={unlocking || !password}
              className="mt-4 w-full rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-50 px-4 py-2.5 text-sm font-bold text-white">
              {unlocking ? "Memeriksa…" : "Buka laporan"}
            </button>
          </form>
        </div>
      </Shell>
    );
  }

  const p = data.payload || {};
  const v = p.view || {};
  const downloadPdf = () => {
    if (data.kind !== "klasik") return;
    openDeepAnalysisPrint({ ...p, audit: null });
  };

  return (
    <Shell>
      {/* Top bar: who shared what, and the way out to Landhep */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <a href="https://landhep.id" className="text-base font-extrabold tracking-wide text-[#14543e]">LANDHEP</a>
            <span className="h-5 w-px bg-slate-200" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 truncate">{data.title}</p>
              <p className="text-[10px] text-slate-400 truncate">
                {KIND_LABEL[data.kind]}{data.ownerLabel ? ` · dibagikan oleh ${data.ownerLabel}` : ""} · {fmtDate(data.createdAt)}
                {data.expiresAt ? ` · berlaku sampai ${fmtDate(data.expiresAt)}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-1">
              <Camera className="w-3 h-3" /> Snapshot saat dibagikan
            </span>
            {data.kind === "klasik" && (
              <button onClick={downloadPdf}
                className="flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 px-3.5 py-2 text-xs font-bold text-white transition-colors">
                <Download className="w-3.5 h-3.5" /> Unduh PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {data.kind === "klasik" ? (
        <>
          <DeepAnalysisHero
            gridLabel={v.gridLabel} fullLocation={v.fullLocation} result={p.result} resolution={v.resolution}
            lat={v.lat} lng={v.lng} verdict={v.verdict} score={v.score} idx={v.idx} yourFit={v.yourFit}
            subcategory={v.subcategory} targetSes={v.targetSes} radius={v.radius} generatedAt={p.generatedAt}
          />
          <div className="max-w-6xl mx-auto px-6 py-6">
            <DeepAnalysisReport result={p.result} targetSes={v.targetSes} radius={v.radius}
              profileView={p.profileView} showBonus={p.showBonus ?? true} />
          </div>
        </>
      ) : (
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {data.kind === "potensi_bisnis"
              ? <BisnisReport report={p.report} />
              : <IklanReport report={p.report} />}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 pb-10 text-center">
        <p className="text-[11px] text-slate-400">
          Laporan ini dibuat di <a href="https://landhep.id" className="font-bold text-brand-700 hover:underline">Landhep</a>, platform intelijen lokasi usaha. Angka bersifat perkiraan — verifikasi di lapangan sebelum memutuskan.
        </p>
      </div>
    </Shell>
  );
}
